import { createBrowserInspector } from '@statelyai/inspect';
import { createActorContext } from '@xstate/react';
import config from 'config';
import { screenerStateChangeListener } from 'domains/signup/data/mixpanelTracking';
import { screenerMachine, ScreenerState } from 'domains/signup/screener/screenerMachine';
import { isArray } from 'lodash';
import { useEffectOnce } from 'react-use';

const shouldUseInspector = config.get('env') === 'development' && config.get('xstateDebugger') === 'true';
const inspect = shouldUseInspector ? createBrowserInspector().inspect : undefined;

export const ScreenerContext = createActorContext(screenerMachine, {
  inspect,
});

/** Needs to be a component in order to access the nested context */
const MixpanelSubscriber = () => {
  const actorRef = ScreenerContext.useActorRef();

  useEffectOnce(() => {
    return actorRef.subscribe(({ value }) => {
      screenerStateChangeListener(value as ScreenerState);
    }).unsubscribe;
  });

  return null;
};

type WithScreenerContext = <P = Record<string, unknown>>(Component: React.FC<P>) => React.FC<P>;

export const withScreenerProvider: WithScreenerContext = Component => {
  return props => {
    return (
      <ScreenerContext.Provider
        options={{
          input: {},
        }}
      >
        <MixpanelSubscriber />
        <Component {...props} />
      </ScreenerContext.Provider>
    );
  };
};

export const withScreenerMachineState = (Component: React.FC, matchingState: ScreenerState | ScreenerState[]) => {
  return (props: Record<string, unknown>) => {
    const matchesState = ScreenerContext.useSelector(state => {
      if (isArray(matchingState) && matchingState.some(s => state.matches(s))) {
        return true;
      }

      if (state.matches(matchingState as ScreenerState)) {
        return true;
      }

      return false;
    });

    if (matchesState) {
      return <Component {...props} />;
    }
    return null;
  };
};
